import type { Subscription } from '../../../types';

import { EventScopeProvider, useViewTracking } from '@blueshift-ui/analytics';
import CircularProgress from '@blueshift-ui/core/dist/components/circular-progress';
import NoUpdateAvailable from '../../../components/membership/update/views/no-update-available';
import Stack from '@blueshift-ui/core/dist/components/stack';
import UpdateMembershipOptions from '../../../components/membership/update/views/options';
import UpdatePending from '../../../components/membership/update/views/update-pending';
import UpgradeToMembership from '../../../components/membership/upgrade-to-member';
import useAuthenticatedUser from '@blueshift-ui/auth/dist/hooks/use-authenticated-user';
import { useMemo } from 'react';
import { useParams } from 'react-router-dom';
import useSubscription from '../../../hooks/subscription/use-subscription';

interface ViewProps {
  subscription: Subscription;
  updateState?: 'update-inactive' | 'update-pending' | 'update-ineligible';
}

function _View({ subscription, updateState }: ViewProps) {
  switch (updateState) {
    case 'update-inactive': {
      return (
        <EventScopeProvider eventScope={{ pageSection: 'update_membership_plan' }}>
          <UpdateMembershipOptions subscription={subscription} />
        </EventScopeProvider>
      );
    }
    case 'update-pending': {
      return (
        <EventScopeProvider eventScope={{ pageSection: 'update_pending_membership' }}>
          <UpdatePending currentSubscription={subscription} />
        </EventScopeProvider>
      );
    }
    case 'update-ineligible': {
      return (
        <EventScopeProvider eventScope={{ pageSection: 'update_ineligible_membership' }}>
          <NoUpdateAvailable />
        </EventScopeProvider>
      );
    }
    default:
      return (
        <EventScopeProvider eventScope={{ pageSection: 'upgrade_to_membership' }}>
          <UpgradeToMembership />
        </EventScopeProvider>
      );
  }
}

function MembershipUpdatePage() {
  const { subscriptionId } = useParams<{ subscriptionId: string }>();
  const { isLoading: isLoadingUser, data: user } = useAuthenticatedUser();
  const { data: subscription, isLoading: subscriptionLoading } = useSubscription(subscriptionId, {
    params: { include: 'upgrade_downgrade_options', jurisdiction_id: user?.jurisdiction_id },
  });

  const updateState = useMemo(() => {
    if (subscriptionLoading) {
      return;
    }

    if (subscription?.pending_transformation) {
      return 'update-pending';
    }

    if (subscription?.update_ineligible_reasons?.length) {
      return 'update-ineligible';
    }

    return 'update-inactive';
  }, [subscription, subscriptionLoading]);

  useViewTracking({
    enabled: Boolean(updateState),
    trackedProperties: {
      content_name: 'Membership Update',
      page_section: 'update_membership',
    },
    type: 'content',
  });

  if (isLoadingUser || subscriptionLoading || !subscription) {
    return (
      <Stack>
        <CircularProgress />
      </Stack>
    );
  }

  return <_View subscription={subscription} updateState={updateState} />;
}

export default MembershipUpdatePage;
